/**
 * Mockups del Sistema de Ayuda Contextual para Dashboard de Negocio.
 *
 * Ruta oculta: /mockups/help-system
 * No está enlazada desde ningún lugar del sitio. Solo accesible por URL directa.
 *
 * Muestra las 4 capas del sistema propuesto:
 *  - Capa 1: Tour guiado la primera vez (react-joyride style)
 *  - Capa 2: Tooltips (?) contextuales en cada pestaña y KPI
 *  - Capa 3: Empty states educativos
 *  - Capa 4: Botón flotante de ayuda con búsqueda + FAQ (sin videos, sin soporte, sin llamada)
 *  - Capa 5: Banners informativos dismissibles al entrar a cada pestaña
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import {
  HelpCircle, X, Search, Calendar as CalendarIcon, DollarSign, TrendingUp,
  Users, ChevronRight, Sparkles, ArrowRight, Info, MessageSquareText,
  Clock, ClipboardList, Image, Briefcase, CreditCard, CheckCheck,
  BookOpen, Lightbulb,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*  CAPA 2 · Tooltip con icono (?)                                            */
/* -------------------------------------------------------------------------- */
function HelpTooltip({ title, description, example }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-[#e85341]"
          onClick={(e) => e.stopPropagation()}
          data-testid="mock-help-tooltip-trigger"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 border-neutral-200 p-4 text-sm shadow-xl" side="top">
        <div className="mb-1 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#fce8e5]">
            <Info className="h-3.5 w-3.5 text-[#e85341]" />
          </div>
          <h4 className="font-semibold text-neutral-900">{title}</h4>
        </div>
        <p className="text-neutral-600">{description}</p>
        {example ? (
          <div className="mt-3 rounded-lg bg-neutral-50 p-2.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">Ejemplo</p>
            <p className="mt-0.5 text-xs text-neutral-700">{example}</p>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/* -------------------------------------------------------------------------- */
/*  CAPA 4 · Botón flotante de ayuda con búsqueda + FAQ                       */
/* -------------------------------------------------------------------------- */
const FAQ_DATA = [
  {
    category: 'Configuración inicial',
    items: [
      {
        q: '¿Cómo configuro mis horarios de atención?',
        a: 'Ve a la pestaña "Config" (arriba a la derecha) → "Horarios". Ahí eliges los días y horas de cada día. Puedes tener diferentes horarios para cada día de la semana.',
      },
      {
        q: '¿Cómo agrego mis servicios?',
        a: 'En la pestaña "Servicios" toca el botón "+ Agregar servicio". Coloca nombre, duración (en minutos) y precio. Puedes tener servicios ilimitados.',
      },
      {
        q: '¿Cómo agrego a mis empleados?',
        a: 'En la pestaña "Equipo" toca "+ Agregar miembro". Cada empleado puede tener sus propios horarios y recibir sus propias citas.',
      },
    ],
  },
  {
    category: 'Reservas y citas',
    items: [
      {
        q: '¿Qué significa cada color en el calendario?',
        a: 'Azul = Confirmada · Verde = Completada · Amarillo = Pendiente de pago · Rojo = Cancelada. Puedes filtrar por estado desde arriba del calendario.',
      },
      {
        q: '¿Qué pasa si un cliente no llega?',
        a: 'Puedes reportar "cliente no llegó" desde la cita. Bookvia analiza el historial y decide si el cliente pierde su depósito automáticamente.',
      },
      {
        q: '¿Puedo cancelar una cita yo mismo?',
        a: 'Sí. Abre la cita → toca los tres puntos → "Cancelar". Si el cliente ya pagó depósito, Bookvia le devuelve el dinero automáticamente.',
      },
    ],
  },
  {
    category: 'Pagos e ingresos',
    items: [
      {
        q: '¿Cuándo recibo mi dinero?',
        a: 'Los pagos entran a tu cuenta bancaria de Stripe automáticamente 2 días hábiles después de la cita completada.',
      },
      {
        q: '¿Qué comisión cobra Bookvia?',
        a: 'Bookvia cobra una comisión pequeña sobre cada depósito exitoso. Ver detalles en "Config" → "Suscripción".',
      },
      {
        q: '¿Dónde veo mis ganancias?',
        a: 'La tarjeta "Ingresos mes" arriba del dashboard. Para reportes detallados: pestaña "Reportes" (por semana, mes, servicio, empleado).',
      },
    ],
  },
  {
    category: 'Perfil y marketing',
    items: [
      {
        q: '¿Cómo hago que mi negocio aparezca primero?',
        a: 'Completa tu perfil al 100%, sube al menos 5 fotos de alta calidad, define servicios con precios y responde reseñas. Bookvia prioriza negocios completos.',
      },
      {
        q: '¿Cómo comparto mi perfil con mis clientes?',
        a: 'Botón "Compartir" arriba del dashboard. Copia el link o envíalo directo por WhatsApp.',
      },
    ],
  },
];

function HelpFloatingButton() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = query.trim()
    ? FAQ_DATA.map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (it) =>
            it.q.toLowerCase().includes(query.toLowerCase()) ||
            it.a.toLowerCase().includes(query.toLowerCase()),
        ),
      })).filter((cat) => cat.items.length > 0)
    : FAQ_DATA;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#e85341] text-white shadow-lg shadow-[#e85341]/30 transition-all hover:scale-105 hover:bg-[#d64431]"
          data-testid="mock-floating-help-btn"
          aria-label="Ayuda"
        >
          <HelpCircle className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto p-0 sm:max-w-lg">
        {/* Header */}
        <SheetHeader className="border-b border-neutral-100 bg-gradient-to-br from-[#fff8f6] to-white px-6 py-5">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Lightbulb className="h-5 w-5 text-[#e85341]" />
            Centro de Ayuda rápida
          </SheetTitle>
          <SheetDescription className="text-neutral-500">
            Encuentra respuestas rápidas a las preguntas más comunes.
          </SheetDescription>
        </SheetHeader>

        {/* Buscador */}
        <div className="border-b border-neutral-100 px-6 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar... ej. horarios, pagos, cancelar"
              className="pl-9"
              data-testid="mock-help-search-input"
            />
          </div>
        </div>

        {/* FAQ agrupadas */}
        <div className="px-6 py-4">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-center">
              <p className="text-sm text-neutral-500">
                No encontramos preguntas para <span className="font-medium">&quot;{query}&quot;</span>.
              </p>
              <a
                href="/ayuda"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#e85341] hover:underline"
              >
                Ir al Centro de Ayuda completo
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          ) : (
            filtered.map((cat) => (
              <div key={cat.category} className="mb-5">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                  {cat.category}
                </p>
                <Accordion type="single" collapsible className="space-y-1">
                  {cat.items.map((it, idx) => (
                    <AccordionItem
                      key={idx}
                      value={`${cat.category}-${idx}`}
                      className="rounded-lg border border-neutral-100 bg-white px-3"
                    >
                      <AccordionTrigger className="py-3 text-left text-sm font-medium hover:no-underline">
                        {it.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-3 pt-0 text-sm text-neutral-600">
                        {it.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          )}
        </div>

        {/* CTA discreto al final: si no encontraste, aquí está el centro de ayuda */}
        <div className="border-t border-neutral-100 bg-neutral-50 px-6 py-4">
          <p className="mb-2 text-xs text-neutral-500">¿No encontraste lo que buscabas?</p>
          <a
            href="/ayuda"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#e85341] hover:underline"
          >
            <BookOpen className="h-4 w-4" />
            Ver Centro de Ayuda completo
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------------------------------------------------------------- */
/*  CAPA 5 · Banner informativo dismissible                                   */
/* -------------------------------------------------------------------------- */
function InfoBanner({ text, storageKey }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-[#fbdcd6] bg-[#fff5f2] px-4 py-3">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#e85341]" />
      <p className="flex-1 text-sm text-neutral-700">{text}</p>
      <button
        onClick={() => setDismissed(true)}
        className="text-neutral-400 transition-colors hover:text-neutral-700"
        data-testid={`mock-dismiss-${storageKey}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CAPA 1 · Tour guiado con overlay                                          */
/* -------------------------------------------------------------------------- */
const TOUR_STEPS = [
  {
    target: 'kpis',
    title: '👋 ¡Bienvenido a tu Dashboard!',
    body: 'Estas 4 tarjetas te dan un resumen instantáneo de tu negocio: citas de hoy, confirmadas, ingresos y total histórico.',
  },
  {
    target: 'tabs',
    title: '📑 Estas son tus herramientas',
    body: 'Cada pestaña tiene una función específica. Vamos a recorrerlas — puedes saltarte el tour cuando quieras.',
  },
  {
    target: 'tab-agenda',
    title: '📅 Agenda',
    body: 'Tu calendario de reservas. Aquí ves las citas del día seleccionado y su estado.',
  },
  {
    target: 'tab-servicios',
    title: '💇 Servicios',
    body: 'Configura qué vendes: nombre, duración, precio. Sin servicios, los clientes no pueden reservar.',
  },
  {
    target: 'tab-equipo',
    title: '👥 Equipo',
    body: 'Agrega a tus empleados/estilistas. Cada uno puede tener sus propios horarios y clientes.',
  },
  {
    target: 'reception',
    title: '🎯 Recepción',
    body: 'Modo caja registradora: úsalo cuando un cliente llega sin cita previa y quieres registrarlo al momento.',
  },
];

function GuidedTour({ open, onClose }) {
  const [step, setStep] = useState(0);
  if (!open) return null;
  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-2">
          <Badge className="bg-[#e85341] text-white hover:bg-[#e85341]">
            Paso {step + 1} de {TOUR_STEPS.length}
          </Badge>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="text-xs text-neutral-500 hover:text-neutral-800"
            data-testid="mock-tour-skip"
          >
            Saltar tour
          </button>
        </div>
        <h3 className="mb-2 text-xl font-semibold text-neutral-900">{current.title}</h3>
        <p className="mb-6 text-neutral-600">{current.body}</p>
        {/* Progreso */}
        <div className="mb-5 flex gap-1">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-[#e85341]' : 'bg-neutral-200'
              }`}
            />
          ))}
        </div>
        <div className="flex justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="mock-tour-prev"
          >
            Anterior
          </Button>
          <Button
            onClick={() => (isLast ? onClose() : setStep((s) => s + 1))}
            className="bg-[#e85341] hover:bg-[#d64431]"
            data-testid="mock-tour-next"
          >
            {isLast ? '¡Empezar!' : 'Siguiente'}
            {!isLast ? <ChevronRight className="ml-1 h-4 w-4" /> : null}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CAPA 3 (v2) · Botón "Tips Bookvia" — contextual por pestaña               */
/*  El calendario/contenido de la pestaña SIEMPRE queda visible.              */
/*  El botón abre un modal con tips prácticos específicos de esa sección.     */
/* -------------------------------------------------------------------------- */
function BookviaTipsButton({ tabName, tips }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#fbdcd6] bg-[#fff5f2] px-3 py-1.5 text-xs font-medium text-[#e85341] transition-all hover:border-[#e85341] hover:bg-[#fce8e5]"
        data-testid="mock-bookvia-tips-btn"
      >
        <Lightbulb className="h-3.5 w-3.5" />
        Tips de Bookvia
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#e85341] to-[#d64431] px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-white/80">
                    Tip de Bookvia · {tabName}
                  </p>
                  <h3 className="text-xl font-bold">Cómo aprovechar mejor esta sección</h3>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-full p-1 transition-colors hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Tips */}
            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
              <div className="space-y-4">
                {tips.map((tip, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#fff5f2] text-lg">
                      {tip.icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">{tip.title}</p>
                      <p className="mt-0.5 text-sm text-neutral-600">{tip.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-100 bg-neutral-50 px-6 py-4 text-center">
              <button
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-neutral-600 hover:text-neutral-900"
              >
                Cerrar y aplicar tips
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

// Tips específicos por pestaña (cada pestaña tendrá su propio contenido)
const AGENDA_TIPS = [
  { icon: '📱', title: 'Comparte tu perfil por WhatsApp', body: 'Toca "Compartir" arriba y mándale tu link a tus clientes actuales. Es la forma #1 de conseguir tus primeras 10 reservas.' },
  { icon: '📸', title: 'Sube al menos 5 fotos profesionales', body: 'Negocios con fotos de calidad reciben hasta 40% más reservas. Ve a la pestaña "Fotos".' },
  { icon: '⭐', title: 'Pide reseñas a tus mejores clientes', body: 'Después de una cita completada, pídele al cliente que te deje reseña en Bookvia. 5 reseñas = subir en el ranking.' },
  { icon: '⏰', title: 'Ofrece horarios flexibles al inicio', body: 'Sábados/domingos y horarios nocturnos tienen menos competencia. Considera abrir esos horarios los primeros meses.' },
  { icon: '💰', title: 'Reduce depósitos temporalmente', body: 'Si tienes pocas citas, considera bajar el porcentaje de depósito requerido en Config → Pagos. Puedes subirlo cuando ya tengas clientela.' },
];

const SERVICIOS_TIPS = [
  { icon: '💵', title: 'Investiga precios de tu competencia', body: 'Busca 3-5 negocios similares en Bookvia y compara precios. Ajusta los tuyos para ser competitivo sin devaluar.' },
  { icon: '⏱️', title: 'Sé realista con las duraciones', body: 'Si un corte real toma 45 min, no pongas 30 min. Los clientes se molestan cuando esperan.' },
  { icon: '📦', title: 'Ofrece paquetes/combos', body: 'Combos como "Corte + Barba" con 10% descuento aumentan el ticket promedio.' },
];

const FOTOS_TIPS = [
  { icon: '☀️', title: 'Usa luz natural', body: 'Toma fotos de día cerca de una ventana. Es gratis y se ve profesional.' },
  { icon: '📐', title: 'Mínimo 5 fotos, máximo 15', body: 'Muestra: fachada, interior, resultados de tu trabajo, tu equipo.' },
  { icon: '❌', title: 'Evita fotos de stock', body: 'Los clientes detectan las fotos genéricas. Tus fotos reales generan más confianza.' },
];

const CLIENTES_TIPS = [
  { icon: '📊', title: 'Identifica a tus mejores clientes', body: 'Ordena por "más frecuentes" y contáctalos para pedirles reseñas o promociones exclusivas.' },
  { icon: '🎂', title: 'Aprovecha cumpleaños', body: 'Ofrece un descuento especial en el mes de cumpleaños de cada cliente para fidelizarlos.' },
];

/* -------------------------------------------------------------------------- */
/*  PÁGINA PRINCIPAL DE MOCKUPS                                               */
/* -------------------------------------------------------------------------- */
export default function HelpSystemMockupsPage() {
  const [tourOpen, setTourOpen] = useState(false);

  const kpis = [
    {
      icon: CalendarIcon, label: 'Citas hoy', value: '0',
      tip: {
        title: 'Citas hoy',
        description: 'Total de reservas programadas para hoy, sin importar el estado.',
        example: 'Si tienes 3 reservas confirmadas y 1 cancelada para hoy, aquí verás "3" (solo cuenta las activas).',
      },
    },
    {
      icon: Clock, label: 'Confirmadas', value: '4',
      tip: {
        title: 'Confirmadas',
        description: 'Reservas ya pagadas o aceptadas por ti. Cuentan para tus ingresos.',
        example: 'Cuando un cliente paga el depósito, la reserva pasa de "Pendiente" a "Confirmada" automáticamente.',
      },
    },
    {
      icon: DollarSign, label: 'Ingresos mes', value: '$3,600',
      tip: {
        title: 'Ingresos del mes',
        description: 'Suma de todos los pagos que has recibido en el mes calendario actual.',
        example: 'Se actualiza en tiempo real. Para reportes detallados por servicio o empleado, ve a "Reportes".',
      },
    },
    {
      icon: TrendingUp, label: 'Total citas', value: '27',
      tip: {
        title: 'Total citas',
        description: 'Contador histórico de todas las reservas que has recibido desde que abriste tu negocio en Bookvia.',
      },
    },
  ];

  const tabs = [
    { id: 'agenda', label: 'Agenda', icon: CalendarIcon, tip: {
      title: 'Agenda', description: 'Tu calendario de reservas. Aquí ves las citas del día seleccionado y su estado (confirmada, completada, cancelada, etc.).',
    } },
    { id: 'reportes', label: 'Reportes', icon: TrendingUp, tip: {
      title: 'Reportes', description: 'Análisis detallado de tus ingresos, servicios más vendidos y clientes frecuentes.',
      example: 'Descubre a qué hora tienes más demanda y qué servicio te genera más ingresos.',
    } },
    { id: 'clientes', label: 'Clientes', icon: Users, tip: {
      title: 'Clientes', description: 'Base de datos de todos los clientes que han reservado contigo.',
      example: 'Puedes ver el historial, frecuencia y ticket promedio de cada cliente.',
    } },
    { id: 'servicios', label: 'Servicios', icon: Briefcase, tip: {
      title: 'Servicios', description: 'Qué vendes en tu negocio. Sin servicios configurados, los clientes NO pueden reservar contigo.',
      example: 'Corte de cabello - 30 min - $200 · Manicure - 45 min - $150',
    } },
    { id: 'equipo', label: 'Equipo', icon: Users, tip: {
      title: 'Equipo', description: 'Agrega a tus empleados/estilistas. Cada uno puede tener horarios propios y recibir sus propias citas.',
    } },
    { id: 'cierres', label: 'Cierres', icon: ClipboardList, tip: {
      title: 'Cierres', description: 'Días u horarios en los que tu negocio NO trabaja: vacaciones, feriados, capacitaciones.',
      example: 'Bloquea el 24-25 diciembre y ningún cliente podrá reservar esos días.',
    } },
    { id: 'fotos', label: 'Fotos', icon: Image, tip: {
      title: 'Fotos', description: 'Galería de tu negocio. Suele ser el primer contacto visual del cliente antes de reservar.',
      example: 'Fotos profesionales aumentan tus reservas hasta 40%. Sube mínimo 5 fotos.',
    } },
    { id: 'suscripcion', label: 'Suscripción', icon: CreditCard, tip: {
      title: 'Suscripción', description: 'Estado de tu plan de Bookvia. Aquí facturas, cambias de plan o cancelas tu suscripción.',
    } },
  ];

  return (
    <div className="min-h-screen bg-[#fdf9f7] pb-24">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Cabecera de la página de mockups */}
        <div className="mb-6 rounded-2xl bg-gradient-to-br from-[#e85341] to-[#d64431] px-6 py-5 text-white shadow-lg">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-white/80">Mockup interactivo</p>
              <h1 className="text-2xl font-bold">Sistema de Ayuda Contextual</h1>
              <p className="mt-1 text-sm text-white/90">
                Prueba cada capa haciendo clic en los elementos. Todo es funcional.
              </p>
            </div>
            <Button
              onClick={() => setTourOpen(true)}
              className="bg-white text-[#e85341] hover:bg-white/90"
              data-testid="mock-launch-tour"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Ver Capa 1 (tour)
            </Button>
          </div>
        </div>

        {/* Capa 5 · Banner de bienvenida (dismissible) */}
        <InfoBanner
          storageKey="dashboard-welcome"
          text="👋 ¡Bienvenido a tu Dashboard! Aquí gestionas todo tu negocio. Toca el ícono (?) en cualquier lado para ver de qué se trata cada sección."
        />

        {/* Panel del dashboard simulado */}
        <Card className="border-neutral-200 bg-white p-6 shadow-sm">
          {/* Header negocio */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-neutral-200 to-neutral-300" />
              <div>
                <p className="text-lg font-semibold text-neutral-900">kratos fit and gym</p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100">Aprobado</Badge>
                  <span className="text-xs text-neutral-500">⭐ 4.0 (6)</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">Ver perfil</Button>
              <Button size="sm" className="bg-[#e85341] hover:bg-[#d64431]">
                Recepción
                <HelpTooltip
                  title="Recepción"
                  description="El modo caja registradora. Úsalo cuando un cliente llega sin reservar y quieres atenderlo al momento."
                  example="Ideal para walk-ins en salones/barberías."
                />
              </Button>
            </div>
          </div>

          {/* Capa 2 · KPIs con tooltip (?) */}
          <div id="kpis" className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-neutral-100 bg-white p-4 transition-shadow hover:shadow-sm">
                <div className="mb-2 flex items-center gap-1.5">
                  <kpi.icon className="h-4 w-4 text-neutral-400" />
                  <span className="text-xs text-neutral-500">{kpi.label}</span>
                  <HelpTooltip {...kpi.tip} />
                </div>
                <p className="text-2xl font-bold text-neutral-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Capa 2 · Tabs con tooltip (?) */}
          <div id="tabs" className="mb-4 flex flex-wrap gap-1 border-b border-neutral-200 pb-1">
            {tabs.map((tab, i) => (
              <button
                key={tab.id}
                id={`tab-${tab.id}`}
                className={`flex items-center gap-1.5 rounded-t-lg px-3 py-2 text-sm transition-colors ${
                  i === 0 ? 'border-b-2 border-[#e85341] text-[#e85341] font-medium' : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
                <HelpTooltip {...tab.tip} />
              </button>
            ))}
          </div>

          {/* Capa 5 · Banner de contexto de la pestaña actual (Agenda) */}
          <InfoBanner
            storageKey="tab-agenda"
            text="ℹ️ En Agenda ves todas las citas del día seleccionado. Toca una fecha del calendario para ver sus reservas."
          />

          {/* Capa 3 (v2) · Header de contenido con botón "Tips Bookvia" */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-900">Citas del 29 de julio de 2026</p>
              <div className="mt-1 flex items-center gap-3 text-xs text-neutral-500">
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />Confirmada</span>
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500" />Completada</span>
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />Pendiente</span>
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-500" />Cancelada</span>
              </div>
            </div>
            <BookviaTipsButton tabName="Agenda" tips={AGENDA_TIPS} />
          </div>

          {/* Calendario + citas del día (siempre visible, aunque esté vacío) */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Calendario simulado */}
            <div className="rounded-xl border border-neutral-100 bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-neutral-900">Calendario</p>
              <div className="mb-2 flex items-center justify-between">
                <button className="text-neutral-400 hover:text-neutral-700">
                  <ChevronRight className="h-4 w-4 rotate-180" />
                </button>
                <p className="text-sm font-medium text-neutral-700">julio 2026</p>
                <button className="text-neutral-400 hover:text-neutral-700">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs">
                {['lu', 'ma', 'mi', 'ju', 'vi', 'sa', 'do'].map((d) => (
                  <div key={d} className="py-1 text-neutral-400">{d}</div>
                ))}
                {[29, 30, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 1, 2].map((n, i) => (
                  <div
                    key={i}
                    className={`rounded-full py-1.5 ${
                      n === 29 && i === 30
                        ? 'bg-neutral-900 text-white font-semibold'
                        : (i < 2 || i > 32)
                          ? 'text-neutral-300'
                          : 'text-neutral-700 hover:bg-neutral-100 cursor-pointer'
                    }`}
                  >
                    {n}
                  </div>
                ))}
              </div>
            </div>

            {/* Panel de citas del día (vacío pero visible) */}
            <div className="rounded-xl border border-neutral-100 bg-white p-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-50">
                <CalendarIcon className="h-5 w-5 text-neutral-400" />
              </div>
              <p className="text-sm text-neutral-500">No hay citas para este día</p>
              <p className="mt-1 text-xs text-neutral-400">Selecciona otra fecha o revisa los tips de Bookvia para conseguir más reservas.</p>
            </div>
          </div>
        </Card>

        {/* Explicación de las capas debajo */}
        <div className="mt-10 space-y-4">
          <h2 className="text-xl font-bold text-neutral-900">Resumen de las 4 capas implementadas</h2>
          {[
            {
              icon: Sparkles, title: 'Capa 1 · Tour guiado la primera vez',
              desc: 'Se lanza automáticamente en el primer login del dueño. 6 pasos. Botón "Saltar tour" siempre visible. Se puede volver a ver desde el botón flotante.',
              test: 'Toca el botón "Ver Capa 1 (tour)" arriba para probarlo.',
            },
            {
              icon: HelpCircle, title: 'Capa 2 · Tooltips (?) contextuales',
              desc: 'Icono (?) al lado de cada KPI y cada pestaña. Al tocar aparece un popover con título, descripción y ejemplo real.',
              test: 'Toca cualquier ícono (?) en las 4 tarjetas o las 8 pestañas.',
            },
            {
              icon: CheckCheck, title: 'Capa 3 · Botón "Tips Bookvia" contextual',
              desc: 'El calendario/contenido de cada pestaña SIEMPRE está visible. Junto al título hay un botón discreto "💡 Tips de Bookvia" que al tocarlo abre un modal con 3-5 tips prácticos ESPECÍFICOS de esa pestaña (Agenda = cómo conseguir citas, Servicios = cómo poner precios, Fotos = cómo tomar mejores fotos, etc.).',
              test: 'Toca el botón coral "💡 Tips de Bookvia" arriba del calendario.',
            },
            {
              icon: Search, title: 'Capa 4 · Botón flotante de ayuda (búsqueda + FAQ)',
              desc: 'Botón (?) flotante bottom-right. Solo búsqueda y FAQ organizadas por categoría. Sin videos, sin soporte, sin llamadas. Si el usuario no encuentra respuesta, lo mandamos a /ayuda (Centro completo).',
              test: 'Toca el botón coral abajo a la derecha.',
            },
            {
              icon: MessageSquareText, title: 'Capa 5 · Banners informativos dismissibles',
              desc: 'Al entrar a cada pestaña la primera vez, banner suave arriba explicando qué es. Al cerrarlo se recuerda y no vuelve a aparecer.',
              test: 'Mira los 2 banners coral suave arriba del dashboard (puedes cerrarlos).',
            },
          ].map((layer) => (
            <div key={layer.title} className="flex gap-4 rounded-xl border border-neutral-100 bg-white p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#fff5f2]">
                <layer.icon className="h-5 w-5 text-[#e85341]" />
              </div>
              <div>
                <p className="font-semibold text-neutral-900">{layer.title}</p>
                <p className="text-sm text-neutral-600">{layer.desc}</p>
                <p className="mt-1 text-xs italic text-[#e85341]">💡 {layer.test}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Componentes flotantes */}
      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <HelpFloatingButton />
    </div>
  );
}
